import { checkoutPayFlowController } from '@cart-services/core/checkoutPayFlowController'
import * as paymentService from '@cart-services/core/paymentService'
import { type ApplePayDialogContext } from '@cart-services/hooks/useApplePayDialogActions'
import { type CountryCode, PAYMENT_EXCEPTION_CODES } from '@constants'
import { datadogRum } from '@datadog/browser-rum'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiFpPaymentExecution } from '@hooks-api/fortune/useApiFpPaymentExecution'
import { useApiGetFpPayment } from '@hooks-api/fortune/useApiGetFpPayment'
import { ECommerceOrderType } from '@hooks-api/types/dataModel/eCommercePayments'
import { type BaseCheckoutPayFlowModule } from '@payments/lib/checkout-pay/BaseCheckoutPayFlowModule'
import { useRef, useState } from 'react'

export interface UseFpB2bApplePayDialogFlowParams {
  paymentModule: BaseCheckoutPayFlowModule
  countryCode: CountryCode
}

/**
 * B2B 版本的 Apple Pay dialog flow hook，對齊 B2C `useApplePayDialogActions` shape，
 * 但內部 API 換成 fastpass 的 `/payment/payment`（init）跟 `/payment/execution`（execute）。
 * 配合 shared `DialogApplePayConfirm`（libs/cart-ui）使用。
 * 訂單於進 dialog 前已由 caller 建好，context 帶真交易單 id。
 */
export const useFpB2bApplePayDialogFlow = ({
  paymentModule,
  countryCode,
}: UseFpB2bApplePayDialogFlowParams) => {
  const { trigger: getPayment } = useApiGetFpPayment()
  const { trigger: executePayment } = useApiFpPaymentExecution()

  const activeCkoMountRef = useRef<paymentService.CkoMount | null>(null)
  const [isExecuting, setIsExecuting] = useState(false)

  const mountApplePayButton = useCallbackEvent(async (context: ApplePayDialogContext, containerId: string) => {
    const initResult = await getPayment({
      order_type: ECommerceOrderType.TRANSACTION,
      order_id: context.paymentTransactionId,
      payment_method_id: context.paymentOptionId,
      remember_payment_card_token: false,
      installment: null,
      payment_success_url: context.paymentSuccessUrl,
      payment_fallback_url: context.paymentFallbackUrl,
    })
    const paymentInfo = initResult?.data?.data
    if (!paymentInfo) {
      throw new Error('[useFpB2bApplePayDialogFlow] getPayment returned empty paymentInfo')
    }
    activeCkoMountRef.current = await paymentService.applyCkoSession(paymentModule, paymentInfo, paymentModule.getAppEnv(), containerId, { countryCode })
  })

  const awaitAndExecutePayment = useCallbackEvent(async (context: ApplePayDialogContext) => {
    const ckoPaymentId = await checkoutPayFlowController.awaitPaymentId({
      paymentTransactionId: context.paymentTransactionId,
      orderHash: context.orderHash,
    })
    setIsExecuting(true)
    datadogRum.addAction('applePayDialog.paymentCompleted', {
      paymentTransactionId: context.paymentTransactionId,
      ckoPaymentId,
    })
    const { data: paymentResult } = await paymentService.executeWithCkoReconciliationRetry(() => executePayment({
      payment_transaction_id: context.paymentTransactionId,
      service_pay_info: { bank_code: null, apple_pay_info: null, pay_token: null },
      user_payment_token_info: null,
      payment_success_url: context.paymentSuccessUrl,
      payment_fallback_url: context.paymentFallbackUrl,
      payment_option_id: context.paymentOptionId,
    }), {
      extraRetryCodes: [PAYMENT_EXCEPTION_CODES.PAYMENT_API_FAILED],
    })
    if (!paymentResult.redirect_url) {
      throw new Error('[useFpB2bApplePayDialogFlow] executePayment returned no redirect_url')
    }
    window.location.href = paymentResult.redirect_url
  })

  const cancelInFlight = useCallbackEvent((reason: string) => {
    setIsExecuting(false)
    checkoutPayFlowController.cancelInFlight(reason)
    activeCkoMountRef.current?.unmount()
    activeCkoMountRef.current = null
  })

  return {
    mountApplePayButton,
    awaitAndExecutePayment,
    cancelInFlight,
    isExecuting,
  }
}
